import React, { useEffect, useState } from 'react';
import { AdminLayout } from '../../components/AdminLayout';
import { analyticsService, AnalyticsData } from '../../services/analyticsService';
import {
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';

const COLORS = ['#10b981', '#3b82f6', '#8b5cf6', '#f59e0b', '#ef4444', '#06b6d4'];

export const AdminAnalytics: React.FC = () => {
  const [data, setData] = useState<AnalyticsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');

  useEffect(() => {
    fetchAnalytics();
    // Rafraîchir toutes les 30 secondes
    const interval = setInterval(fetchAnalytics, 30000);
    return () => clearInterval(interval);
  }, []);

  const fetchAnalytics = async () => {
    try {
      const analyticsData = await analyticsService.getAnalytics();
      setData(analyticsData);
    } catch (error) {
      console.error('Erreur:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading || !data) {
    return (
      <AdminLayout activeSection="analytics">
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-700"></div>
        </div>
      </AdminLayout>
    );
  }

  const tabs = [
    { id: 'overview', label: '📊 Vue d\'ensemble', icon: '📊' },
    { id: 'users', label: '👥 Utilisateurs', icon: '👥' },
    { id: 'content', label: '📝 Contenu', icon: '📝' },
    { id: 'engagement', label: '📈 Engagement', icon: '📈' },
  ];

  // Préparer les données pour les graphiques
  const exposesByTypeData = [
    { name: 'Jeudi Matin', value: data.exposes.by_type.jeudi_matin },
    { name: 'Jeudi Soir', value: data.exposes.by_type.jeudi_soir },
    { name: 'Mercredi Soir', value: data.exposes.by_type.mercredi_soir },
  ];

  const usersByRoleData = [
    { name: 'Membres', value: data.users.members },
    { name: 'Admins', value: data.users.admins },
    { name: 'Visiteurs', value: data.users.visitors },
  ];

  const eventsByTypeData = data.events.by_type.map((item) => ({
    name: item.type_event === 'balayage' ? 'Balayage' : 
          item.type_event === 'reunion' ? 'Réunion' : 
          item.type_event === 'dahira' ? 'Dahira' : 'Autre',
    value: item.count,
  }));

  const livresByCategorieData = data.bibliotheque.livres_by_categorie.map((item) => ({
    name: item.categorie,
    value: item.count,
  }));

  const audiosByCategorieData = data.mediatheque.by_categorie.map((item) => ({
    name: item.categorie,
    value: item.count,
  }));

  const creationLast30DaysData = Object.entries(data.creation_last_30_days).map(([key, value]) => ({
    name: key === 'users' ? 'Utilisateurs' :
          key === 'exposes' ? 'Exposés' :
          key === 'ramadan' ? 'Ramadan' :
          key === 'events' ? 'Événements' :
          key === 'livres' ? 'Livres' :
          key === 'audios' ? 'Audios' :
          key === 'galerie' ? 'Galerie' :
          key === 'contacts' ? 'Contacts' :
          key === 'daara' ? 'Daara' :
          'Autre',
    value: value,
  }));

  return (
    <AdminLayout activeSection="analytics">
      <div className="mb-6">
        <h2 className="text-3xl font-bold text-green-700">Analytics & Statistiques</h2>
        <p className="text-gray-600 mt-1">Données en temps réel de votre plateforme</p>
        <button
          onClick={fetchAnalytics}
          className="mt-4 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
        >
          🔄 Actualiser
        </button>
      </div>

      {/* Onglets */}
      <div className="bg-white rounded-xl shadow-lg p-4 mb-6">
        <div className="flex space-x-2 border-b border-gray-200">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-6 py-3 font-semibold transition-all ${
                activeTab === tab.id
                  ? 'border-b-2 border-green-600 text-green-700'
                  : 'text-gray-600 hover:text-green-700'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* Vue d'ensemble */}
      {activeTab === 'overview' && (
        <div className="space-y-6">
          {/* Cartes de résumé */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="bg-white rounded-xl shadow-lg p-6 border-l-4 border-green-500">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-600 text-sm mb-1">Utilisateurs Actifs</p>
                  <p className="text-3xl font-bold text-green-700">{data.users.active}</p>
                  <p className="text-xs text-gray-500 mt-1">sur {data.users.total} total</p>
                </div>
                <div className="text-4xl">👥</div>
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-lg p-6 border-l-4 border-blue-500">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-600 text-sm mb-1">Total Exposés</p>
                  <p className="text-3xl font-bold text-blue-700">{data.exposes.total}</p>
                  <p className="text-xs text-gray-500 mt-1">{data.exposes.this_month} ce mois</p>
                </div>
                <div className="text-4xl">📝</div>
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-lg p-6 border-l-4 border-purple-500">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-600 text-sm mb-1">Total Audios</p>
                  <p className="text-3xl font-bold text-purple-700">{data.mediatheque.total_audios}</p>
                  <p className="text-xs text-gray-500 mt-1">{data.mediatheque.total_ecoutes} écoutes</p>
                </div>
                <div className="text-4xl">🎵</div>
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-lg p-6 border-l-4 border-amber-500">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-600 text-sm mb-1">Total Événements</p>
                  <p className="text-3xl font-bold text-amber-700">{data.events.total}</p>
                  <p className="text-xs text-gray-500 mt-1">{data.events.upcoming} à venir</p>
                </div>
                <div className="text-4xl">📅</div>
              </div>
            </div>
          </div>

          {/* Nouvelles cartes pour Contact et Daara */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mt-6">
            <div className="bg-white rounded-xl shadow-lg p-6 border-l-4 border-teal-500">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-600 text-sm mb-1">Messages Contact</p>
                  <p className="text-3xl font-bold text-teal-700">{data.contact.total}</p>
                  <p className="text-xs text-gray-500 mt-1">{data.contact.non_repondus} en attente</p>
                </div>
                <div className="text-4xl">📬</div>
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-lg p-6 border-l-4 border-indigo-500">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-600 text-sm mb-1">Cours Daara</p>
                  <p className="text-3xl font-bold text-indigo-700">{data.daara.total}</p>
                  <p className="text-xs text-gray-500 mt-1">{data.daara.total_vues} vues</p>
                </div>
                <div className="text-4xl">📚</div>
              </div>
            </div>
          </div>

          {/* Graphiques de création (30 derniers jours) */}
          <div className="bg-white rounded-xl shadow-lg p-6">
            <h3 className="text-xl font-bold text-gray-800 mb-4">Créations des 30 derniers jours</h3>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={creationLast30DaysData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Bar dataKey="value" fill="#10b981" />
              </BarChart>
            </ResponsiveContainer>
          </div>

          {/* Graphiques en camembert */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="bg-white rounded-xl shadow-lg p-6">
              <h3 className="text-xl font-bold text-gray-800 mb-4">Exposés par Type</h3>
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={exposesByTypeData}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={(entry: any) => `${entry.name}: ${(entry.percent * 100).toFixed(0)}%`}
                    outerRadius={100}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {exposesByTypeData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            </div>

            <div className="bg-white rounded-xl shadow-lg p-6">
              <h3 className="text-xl font-bold text-gray-800 mb-4">Utilisateurs par Rôle</h3>
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={usersByRoleData}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={(entry: any) => `${entry.name}: ${(entry.percent * 100).toFixed(0)}%`}
                    outerRadius={100}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {usersByRoleData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>
      )}

      {/* Onglet Utilisateurs */}
      {activeTab === 'users' && (
        <div className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="bg-white rounded-xl shadow-lg p-6">
              <p className="text-gray-600 text-sm mb-2">Total Utilisateurs</p>
              <p className="text-4xl font-bold text-green-700">{data.users.total}</p>
            </div>
            <div className="bg-white rounded-xl shadow-lg p-6">
              <p className="text-gray-600 text-sm mb-2">Actifs</p>
              <p className="text-4xl font-bold text-blue-700">{data.users.active}</p>
            </div>
            <div className="bg-white rounded-xl shadow-lg p-6">
              <p className="text-gray-600 text-sm mb-2">Inactifs</p>
              <p className="text-4xl font-bold text-gray-700">{data.users.inactive}</p>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-lg p-6">
            <h3 className="text-xl font-bold text-gray-800 mb-4">Inscriptions par mois (6 derniers mois)</h3>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={data.users.by_month}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="label" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Line type="monotone" dataKey="count" stroke="#10b981" strokeWidth={2} />
              </LineChart>
            </ResponsiveContainer>
          </div>

          <div className="bg-white rounded-xl shadow-lg p-6">
            <h3 className="text-xl font-bold text-gray-800 mb-4">Répartition par Rôle</h3>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={usersByRoleData}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                    label={(entry: any) => `${entry.name}: ${(entry.percent * 100).toFixed(0)}%`}
                  outerRadius={100}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {usersByRoleData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {/* Onglet Contenu */}
      {activeTab === 'content' && (
        <div className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="bg-white rounded-xl shadow-lg p-6">
              <p className="text-gray-600 text-sm mb-2">Exposés</p>
              <p className="text-3xl font-bold text-blue-700">{data.exposes.total}</p>
            </div>
            <div className="bg-white rounded-xl shadow-lg p-6">
              <p className="text-gray-600 text-sm mb-2">Ramadan</p>
              <p className="text-3xl font-bold text-purple-700">{data.ramadan.total}</p>
            </div>
            <div className="bg-white rounded-xl shadow-lg p-6">
              <p className="text-gray-600 text-sm mb-2">Livres</p>
              <p className="text-3xl font-bold text-green-700">{data.bibliotheque.livres}</p>
            </div>
            <div className="bg-white rounded-xl shadow-lg p-6">
              <p className="text-gray-600 text-sm mb-2">Audios</p>
              <p className="text-3xl font-bold text-amber-700">{data.mediatheque.total_audios}</p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="bg-white rounded-xl shadow-lg p-6">
              <h3 className="text-xl font-bold text-gray-800 mb-4">Exposés par Type</h3>
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={exposesByTypeData}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={(entry: any) => `${entry.name}: ${(entry.percent * 100).toFixed(0)}%`}
                    outerRadius={100}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {exposesByTypeData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            </div>

            <div className="bg-white rounded-xl shadow-lg p-6">
              <h3 className="text-xl font-bold text-gray-800 mb-4">Événements par Type</h3>
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={eventsByTypeData}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={(entry: any) => `${entry.name}: ${(entry.percent * 100).toFixed(0)}%`}
                    outerRadius={100}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {eventsByTypeData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </div>

          {livresByCategorieData.length > 0 && (
            <div className="bg-white rounded-xl shadow-lg p-6">
              <h3 className="text-xl font-bold text-gray-800 mb-4">Livres par Catégorie</h3>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={livresByCategorieData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="value" fill="#10b981" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}

          {audiosByCategorieData.length > 0 && (
            <div className="bg-white rounded-xl shadow-lg p-6">
              <h3 className="text-xl font-bold text-gray-800 mb-4">Audios par Catégorie</h3>
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={audiosByCategorieData}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={(entry: any) => `${entry.name}: ${(entry.percent * 100).toFixed(0)}%`}
                    outerRadius={100}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {audiosByCategorieData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>
      )}

      {/* Onglet Engagement */}
      {activeTab === 'engagement' && (
        <div className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="bg-white rounded-xl shadow-lg p-6">
              <p className="text-gray-600 text-sm mb-2">Vues Exposés</p>
              <p className="text-4xl font-bold text-blue-700">{data.exposes.total_vues}</p>
            </div>
            <div className="bg-white rounded-xl shadow-lg p-6">
              <p className="text-gray-600 text-sm mb-2">Écoutes Audios</p>
              <p className="text-4xl font-bold text-purple-700">{data.mediatheque.total_ecoutes}</p>
            </div>
            <div className="bg-white rounded-xl shadow-lg p-6">
              <p className="text-gray-600 text-sm mb-2">Téléchargements</p>
              <p className="text-4xl font-bold text-green-700">{data.mediatheque.total_downloads}</p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="bg-white rounded-xl shadow-lg p-6">
              <h3 className="text-xl font-bold text-gray-800 mb-4">Top 5 Exposés les plus vus</h3>
              <div className="space-y-3">
                {data.exposes.top_5.map((expose, index) => (
                  <div key={expose.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div className="flex items-center space-x-3">
                      <span className="text-2xl font-bold text-green-700">#{index + 1}</span>
                      <span className="font-medium">{expose.theme}</span>
                    </div>
                    <span className="text-green-700 font-semibold">{expose.nombre_vues} vues</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-lg p-6">
              <h3 className="text-xl font-bold text-gray-800 mb-4">Top 5 Audios les plus écoutés</h3>
              <div className="space-y-3">
                {data.mediatheque.top_5.map((audio, index) => (
                  <div key={audio.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div className="flex items-center space-x-3">
                      <span className="text-2xl font-bold text-purple-700">#{index + 1}</span>
                      <span className="font-medium">{audio.titre}</span>
                    </div>
                    <span className="text-purple-700 font-semibold">{audio.nombre_ecoutes} écoutes</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-lg p-6">
            <h3 className="text-xl font-bold text-gray-800 mb-4">Statistiques de Notifications</h3>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="p-4 bg-green-50 rounded-lg">
                <p className="text-sm text-gray-600">Total Notifications</p>
                <p className="text-2xl font-bold text-green-700">{data.notifications.total}</p>
              </div>
              <div className="p-4 bg-blue-50 rounded-lg">
                <p className="text-sm text-gray-600">Envoyées</p>
                <p className="text-2xl font-bold text-blue-700">{data.notifications.sent}</p>
              </div>
              <div className="p-4 bg-purple-50 rounded-lg">
                <p className="text-sm text-gray-600">Emails Envoyés</p>
                <p className="text-2xl font-bold text-purple-700">{data.notifications.total_emails_sent}</p>
              </div>
              <div className="p-4 bg-red-50 rounded-lg">
                <p className="text-sm text-gray-600">Échecs</p>
                <p className="text-2xl font-bold text-red-700">{data.notifications.total_emails_failed}</p>
              </div>
            </div>
          </div>

          {/* Statistiques Contact */}
          <div className="bg-white rounded-xl shadow-lg p-6">
            <h3 className="text-xl font-bold text-gray-800 mb-4">Messages de Contact</h3>
            <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
              <div className="p-4 bg-blue-50 rounded-lg">
                <p className="text-sm text-gray-600">Total</p>
                <p className="text-2xl font-bold text-blue-700">{data.contact.total}</p>
              </div>
              <div className="p-4 bg-green-50 rounded-lg">
                <p className="text-sm text-gray-600">Lus</p>
                <p className="text-2xl font-bold text-green-700">{data.contact.lus}</p>
              </div>
              <div className="p-4 bg-orange-50 rounded-lg">
                <p className="text-sm text-gray-600">Non lus</p>
                <p className="text-2xl font-bold text-orange-700">{data.contact.non_lus}</p>
              </div>
              <div className="p-4 bg-purple-50 rounded-lg">
                <p className="text-sm text-gray-600">Répondus</p>
                <p className="text-2xl font-bold text-purple-700">{data.contact.repondus}</p>
              </div>
              <div className="p-4 bg-red-50 rounded-lg">
                <p className="text-sm text-gray-600">En attente</p>
                <p className="text-2xl font-bold text-red-700">{data.contact.non_repondus}</p>
              </div>
            </div>
          </div>

          {/* Statistiques Daara */}
          <div className="bg-white rounded-xl shadow-lg p-6">
            <h3 className="text-xl font-bold text-gray-800 mb-4">Cours Daara par Jour</h3>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={data.daara.by_jour.map((item: any) => ({
                name: item.jour === 'lundi' ? 'Lundi' :
                      item.jour === 'mardi' ? 'Mardi' :
                      item.jour === 'mercredi' ? 'Mercredi' :
                      item.jour === 'jeudi' ? 'Jeudi' :
                      item.jour === 'vendredi' ? 'Vendredi' :
                      item.jour === 'samedi' ? 'Samedi' :
                      'Dimanche',
                value: item.count,
              }))}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Bar dataKey="value" fill="#10b981" />
              </BarChart>
            </ResponsiveContainer>
            <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="p-4 bg-indigo-50 rounded-lg">
                <p className="text-sm text-gray-600">Total Cours</p>
                <p className="text-2xl font-bold text-indigo-700">{data.daara.total}</p>
              </div>
              <div className="p-4 bg-teal-50 rounded-lg">
                <p className="text-sm text-gray-600">Ce mois</p>
                <p className="text-2xl font-bold text-teal-700">{data.daara.this_month}</p>
              </div>
              <div className="p-4 bg-cyan-50 rounded-lg">
                <p className="text-sm text-gray-600">Total Vues</p>
                <p className="text-2xl font-bold text-cyan-700">{data.daara.total_vues}</p>
              </div>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
};

